import { Dimensions, useGraphCtx } from '@mi-front/core';
import { styled } from '@mui/material/styles';
import classNames from 'classnames';
import throttle from 'lodash/throttle';
import { useEffect, useState } from 'react';
import Draggable, { DraggableEventHandler } from 'react-draggable';
import { Marker } from './Marker';

const StyledContainer = styled('div')<{ isDragging?: boolean }>(({ isDragging }) => ({
  float: 'left',
  height: '100%',
  ...(isDragging && {
    position: 'relative',
    zIndex: 1,
  }),
}));

const StyledDragBackground = styled('div')({
  position: 'absolute',
  background: 'transparent',
  left: '-25px',
  width: '50px',
});

const StyledDragHandle = styled('span')({
  width: '24px',
  height: '24px',
  borderRadius: '12px',
  position: 'absolute',
  left: '1px',
  cursor: 'ew-resize',
  zIndex: 50,
  transform: 'translateX(-50%) translateY(-50%)',
});

interface PropsP extends Dimensions {
  readonly x: number;
  readonly ts: number;
  readonly isDragging: boolean;
  readonly color: string;
  readonly onDragStart: DraggableEventHandler;
  readonly onDrag: DraggableEventHandler;
  readonly onDragStop: DraggableEventHandler;
  readonly throttleUpdateMs?: number;
}

export function DraggableMarkerP({ throttleUpdateMs = 50, isDragging = false, ...p }: PropsP) {
  const { onDragStart, onDrag, onDragStop, x, color, height, width } = p;

  return (
    <Draggable
      axis="x"
      handle=".draggable-marker__handle"
      bounds={
        {
          left: 0,
          right: width - 2,
          //top: number
          //bottom: number
        } as any
      }
      position={{
        x,
        y: 0,
      }}
      defaultPosition={{
        x,
        y: 0,
      }}
      onStart={onDragStart}
      onDrag={throttle(onDrag, throttleUpdateMs)}
      onStop={onDragStop}
    >
      <StyledContainer
        isDragging={isDragging}
        className={classNames('el-draggable-marker__container')}
      >
        {isDragging && (
          <StyledDragBackground
            style={{
              height,
            }}
          />
        )}
        <Marker color={color} checkOverflow className="el-draggable-marker" />
        <StyledDragHandle
          className={classNames('draggable-marker__handle')}
          style={{
            top: height / 2,
            background: color,
          }}
        />
      </StyledContainer>
    </Draggable>
  );
}

type Props = Pick<PropsP, 'ts' | 'color'> & {
  readonly onChange: (ts: number, isDragging: boolean) => void;
};

export function DraggableMarker(p: Props) {
  const { scale, width, height } = useGraphCtx();

  const [internalTs, setInternalTs] = useState(p.ts);
  const [isDragging, setDragging] = useState(false);

  useEffect(() => {
    setInternalTs(p.ts);
  }, [p.ts]);

  return (
    <DraggableMarkerP
      {...p}
      width={width}
      height={height}
      x={scale(internalTs)!}
      ts={internalTs}
      isDragging={isDragging}
      onDragStart={(_event, data) => {
        const ts = Number(scale.invert(data.x)!);
        document.body.style.cursor = 'ew-resize';
        setDragging(true);
        p.onChange(ts, true);
      }}
      onDrag={(_event, data) => {
        const ts = Number(scale.invert(data.x)!);
        setInternalTs(ts);
        p.onChange(ts, true);
      }}
      onDragStop={(_event, data) => {
        document.body.style.cursor = 'auto';
        const ts = Number(scale.invert(data.x)!);
        setDragging(false);
        setInternalTs(ts);
        p.onChange(ts, false);
      }}
    />
  );
}
